import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { SelectItem } from 'primeng/api';
import { StripePlans } from 'src/app/models/stripe.model';
import { AuthConfig, AuthService, ResponseObject } from 'src/app/Services/auth.service';
import { ChatGPTContentGenerationRequest, ChatGPTGeneratedProductDescriptionsResponse, ChatGPTGenerationGalleryDescriptions, ChatGPTService } from 'src/app/Services/chatgpt.service';
import { StripeService } from 'src/app/Services/stripe.service';
import { SubscriptionRulesService } from 'src/app/Services/subscription-rules.service';

@Component({
    selector: 'chatgpt-dialog',
    templateUrl: './chatgpt-dialog.component.html',
    inputs: ['displayDialog', 'description', 'artistProductId', 'galleryProductId'],
    outputs: ['onUseGeneratedSuggestion', 'onClose'],
    standalone: false
})
export class ChatgptDialogComponent implements OnInit {
    public isStripeDisplayPlansDialogVisible = false;
    @Output() upgradeDialogVisibleChange = new EventEmitter<boolean>();
    @Output() onclose = new EventEmitter<boolean>();
    public displayDialog!: boolean;
    public description!: string;
    public artistProductId!: string;
    public galleryProductId!: string;
    public onClose = new EventEmitter<void>();
    public onUseGeneratedSuggestion = new EventEmitter<GeneratedProductDescriptionsSuggestions>();
    public request: ChatGPTContentGenerationRequest = {} as ChatGPTContentGenerationRequest;
    public galleryRequest: ChatGPTGenerationGalleryDescriptions = {} as ChatGPTGenerationGalleryDescriptions;
    public showSuggestions = false;
    public aiDescriptionDailogVisible = false;
    public gptGeneratedResponse: ChatGPTGeneratedProductDescriptionsResponse = {} as ChatGPTGeneratedProductDescriptionsResponse;
    public availableTonesOptions: SelectItem[] = [];
    private defaultTone = 'Accurate';
    public isArtist = false;
    public isSuperAdmin: boolean = false;
    public isGallery = false;
    public isAdministrator = false;
    public isAuthenticated = false;
    public isFreePlanUser = false;
    public authConfig!: AuthConfig;
    public stripePlans: StripePlans[] = [];

    constructor(
        private chatGPTService: ChatGPTService,
        private ngxSpinnerService: NgxSpinnerService,
        private subscriptionRulesService: SubscriptionRulesService,
        private authService: AuthService,
        private stripeService: StripeService
    ) {
        this.initializeRequestDefaults();
    }

    public ngOnInit(): void {
        this.initializeAvailableTones();
        this.setRequestData();
        this.checkUserAuthentication();
    }

    private initializeAvailableTones(): void {
        this.availableTonesOptions = [
            { label: 'Accurate', value: 'Accurate' },
            { label: 'Marketing', value: 'Marketing' },
            { label: 'Technical', value: 'Technical' },
            { label: 'Brief', value: 'Brief' },
            { label: 'Emotive', value: 'Emotive' },
            { label: 'Innovative', value: 'Innovative' },
            { label: 'Historical', value: 'Historical' },
            { label: 'Analytical', value: 'Analytical' },
            { label: 'Subjective', value: 'Subjective' },
            { label: 'Inspirational', value: 'Inspirational' }
        ];
    }

    private initializeRequestDefaults(): void {
        // for Artist
        this.request.IncludeProductSeries = true;
        this.request.IncludeProductMaterial = true;
        this.request.IncludeProductFeatures = true;
        this.request.IncludeArtistProfile = true;
        this.request.IncludeProductCoverPhoto = true;
        this.request.UseChatGPTAIEngine = true;

        // for Gallery
        this.galleryRequest.IncludeGalleryProfile = true;
        this.galleryRequest.IncludeProductCoverPhoto = true;
        this.galleryRequest.UseChatGPTAIEngine = true;
    }
    private setRequestData(): void {
        // for Artist
        console.log('ArtistProductId:', this.artistProductId);
        this.request.ArtistProductId = this.artistProductId;
        this.request.Tone = this.defaultTone;
        this.request.HintContent = this.description;
        // for Gallery
        this.galleryRequest.ProductId = this.galleryProductId;
        console.log('galleryProductId:', this.galleryProductId);
        this.galleryRequest.Tone = this.defaultTone;
        this.galleryRequest.HintContent = this.description;
    }

    private checkUserAuthentication(): void {
        this.isAuthenticated = this.authService.isAuthenticated();
        if (this.isAuthenticated) {
            this.authConfig = this.authService.getAuthConfig();
            this.isArtist = this.authConfig.IsArtist || this.authConfig.IsAdministrator || false;
            this.isSuperAdmin = this.authConfig.IsSuperAdmin!;
            this.isGallery = this.authConfig.IsGallery || false;
            this.isFreePlanUser = this.authService.isFreePlanUser();
        }
    }

    public generateChatGPTContent($event: any, useChatGPTAIEngine: boolean): void {
        console.log(this.request);
        this.showSuggestions = false;
        this.gptGeneratedResponse = { Success: true };
        if (this.isSuperAdmin || this.isArtist || this.isAdministrator) {
            // Allow admin to use AI engine as well
            this.request.UseChatGPTAIEngine = true;
        } else if (this.isGallery) {
            this.galleryRequest.UseChatGPTAIEngine = true;
        }

        if (this.isFreePlanUser) {
            this.handleFreePlanUser();
        } else {
            this.ngxSpinnerService.show();
            this.generateDescriptionBasedOnType();
        }
    }

    private handleFreePlanUser(): void {
        this.ngxSpinnerService.show();
        this.subscriptionRulesService.CanGenrateDescriptionWithAi().subscribe(
            (canGenerate: boolean) => {
                this.ngxSpinnerService.hide();
                if (canGenerate) {
                    this.subscriptionRulesService.fetchRemaningAiCalls().subscribe((response: ResponseObject) => {
                        if (response.ResponseData >= 0) {
                            this.ngxSpinnerService.show();
                            this.generateDescriptionBasedOnType();
                        }
                    });
                } else {
                    this.aiDescriptionDailogVisible = true;
                }
            },
            error => {
                console.error('Error checking AI description generation eligibility:', error);
                this.ngxSpinnerService.hide();
            }
        );
    }

    private generateDescriptionBasedOnType(): void {
        if ((this.isArtist || this.isGallery || this.isAdministrator) && this.artistProductId) {
            this.chatGPTService.generateSuggestion(this.request).subscribe(
                response => this.handleSuccessfulResponse(response),
                error => this.handleError(error)
            );
        } else if (this.isGallery && this.galleryProductId) {
            this.chatGPTService.generateGalleryProducutDescriptions(this.galleryRequest).subscribe(
                response => this.handleSuccessfulResponse(response),
                error => this.handleError(error)
            );
        }
    }

    private handleSuccessfulResponse(response: ChatGPTGeneratedProductDescriptionsResponse): void {
        this.gptGeneratedResponse = response;
        this.showSuggestions = this.gptGeneratedResponse.Success;
        this.ngxSpinnerService.hide();
    }

    private handleError(error: any): void {
        console.error(error);
        this.ngxSpinnerService.hide();
    }

    public useChatGPTSuggestion($event: any): void {
        this.onUseGeneratedSuggestion.emit({
            ShortDescription: this.gptGeneratedResponse?.ShortDescription,
            MarketingDescription: this.gptGeneratedResponse?.MarketingDescription,
            SocialMediaPostDescription: this.gptGeneratedResponse?.SocialMediaPostDescription
        });
    }

    public onDialogClosed(): void {
        this.onClose.emit();
    }

    public onCloseLimitDailog($event: any): void {
        this.aiDescriptionDailogVisible = false;
    }

    public ShowUpgradeDailog() {
        this.isStripeDisplayPlansDialogVisible = true;
        // this.getAllPlans();
    }

    closeDialog() {
        this.aiDescriptionDailogVisible = false;
        this.onclose.emit(this.aiDescriptionDailogVisible); // Emit event on close
    }

    public onCloseStripePaymentDailog($event: any) {
        this.isStripeDisplayPlansDialogVisible = $event;
    }
    // public getAllPlans() {
    //     if (this.isArtist || this.isGallery) {
    //         if (this.isFreePlanUser) {
    //             this.ngxSpinnerService.show();
    //             this.stripeService.getAllPlans().subscribe(
    //                 (ApiResponce: StripePlans[]) => {
    //                     console.log(ApiResponce);
    //                     this.stripePlans = ApiResponce;
    //                     this.ngxSpinnerService.hide();
    //                 },
    //                 (error) => { console.error(error); this.ngxSpinnerService.hide(); }
    //             );
    //         }
    //     }
    // }
}

export class GeneratedProductDescriptionsSuggestions {
    ShortDescription?: string;
    MarketingDescription?: string;
    SocialMediaPostDescription?: string;
}
